import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Clock, TrendingUp, AlertTriangle } from "lucide-react";

export default function TimeTracking() {
  const { data: timeRecords = [], isLoading } = useQuery({
    queryKey: ["time-tracking"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("time_tracking")
        .select(`
          *,
          athletes (name, sport)
        `)
        .order("week_start_date", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const stats = {
    total: timeRecords.length,
    compliant: timeRecords.filter((r) => r.compliance_status === "compliant").length,
    warning: timeRecords.filter((r) => r.compliance_status === "warning").length,
    overLimit: timeRecords.filter((r) => r.compliance_status === "over_limit").length,
    avgHours: Math.round(
      timeRecords.reduce((sum, r) => sum + Number(r.total_countable_hours || 0), 0) / timeRecords.length
    ) || 0,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "compliant": return "default";
      case "warning": return "secondary";
      case "over_limit": return "destructive";
      default: return "outline";
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Clock className="h-8 w-8 text-primary" />
            Practice & Activity Time Tracking
          </h1>
          <p className="text-muted-foreground mt-2">
            Monitor NCAA 20-hour weekly limit for countable athletically related activities
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Records</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">Weekly reports</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Compliant Weeks</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.compliant}</div>
              <p className="text-xs text-muted-foreground">Under 20 hours</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Average Hours</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.avgHours}h</div>
              <p className="text-xs text-muted-foreground">Per week</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Violations</CardTitle>
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">{stats.overLimit}</div>
              <p className="text-xs text-muted-foreground">Over 20-hour limit</p>
            </CardContent>
          </Card>
        </div>

        {/* Time Tracking Table */}
        <Card>
          <CardHeader>
            <CardTitle>Weekly Activity Hours</CardTitle>
            <CardDescription>Practice, competition, and NIL activity time tracking</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12 text-muted-foreground">Loading time tracking data...</div>
            ) : timeRecords.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No time tracking records found. Demo data may not be loaded.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Athlete</TableHead>
                    <TableHead>Sport</TableHead>
                    <TableHead>Week Start</TableHead>
                    <TableHead>Practice</TableHead>
                    <TableHead>Competition</TableHead>
                    <TableHead>Strength</TableHead>
                    <TableHead>Meetings</TableHead>
                    <TableHead>NIL</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {timeRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="font-medium">{record.athletes?.name}</TableCell>
                      <TableCell>{record.athletes?.sport}</TableCell>
                      <TableCell>{new Date(record.week_start_date).toLocaleDateString()}</TableCell>
                      <TableCell>{Number(record.practice_hours).toFixed(1)}h</TableCell>
                      <TableCell>{Number(record.competition_hours).toFixed(1)}h</TableCell>
                      <TableCell>{Number(record.strength_training_hours).toFixed(1)}h</TableCell>
                      <TableCell>{Number(record.team_meetings_hours).toFixed(1)}h</TableCell>
                      <TableCell>{Number(record.nil_activity_hours).toFixed(1)}h</TableCell>
                      <TableCell className="font-semibold">
                        {Number(record.total_countable_hours).toFixed(1)}h
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(record.compliance_status)}>
                          {record.compliance_status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
